import { AbstractModuleDto } from '../generated-types/abstractModuleDto';
import { CourseHomePageDto } from '../generated-types/courseHomePageDto';
import { CourseModuleDto } from '../generated-types/courseModuleDto';
import { TrainingPageCourseDto } from '../generated-types/trainingPageCourseDto';
import { AbstractApi } from './AbstractApi';

/**
 * An api to interact with the courses controller
 */
export default class CoursesApi extends AbstractApi {
  /**
   * Get the course id for the specified slug
   * @param slug The slug to get the course id for
   * @returns The course id for the specified slug
   */
  async GetCourseId(slug: string): Promise<string> {
    const response = await this.authFetch(this.BuildUrl(`courses/${slug}`), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    return response.text();
  }

  /**
   * Get the modules for the specified course
   * @param courseId The course to get the modules for
   */
  async GetModules(courseId: string): Promise<CourseModuleDto[]> {
    const response = await this.authFetch(
      this.BuildUrl(`courses/${courseId}/modules`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.json()) as unknown as CourseModuleDto[];
  }

  /**
   * Get a module
   * @param moduleId The id of the module
   * @returns The module matching the specified id
   */
  async GetModule<T extends AbstractModuleDto>(moduleId: string): Promise<T> {
    const response = await this.authFetch(
      this.BuildUrl(`courses/modules/${moduleId}`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.json()) as T;
  }

  /**
   * Complete the specified module id
   * @param moduleId The module to complete
   */
  async CompleteModule(moduleId: string): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl(`courses/modules/${moduleId}/complete`),
      {
        method: 'PUT',
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Get the training page course details
   */
  async GetTrainingPageCourseDetails(): Promise<TrainingPageCourseDto[]> {
    const response = await this.authFetch(this.BuildUrl('courses/training'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    return (await response.json()) as TrainingPageCourseDto[];
  }

  /**
   * Get the course information for the home page
   */
  async GetCoursesForHomePage(): Promise<CourseHomePageDto[]> {
    const response = await fetch(this.BuildUrl('courses/home'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    return (await response.json()) as CourseHomePageDto[];
  }

  /**
   * Get the course card image blob
   * @param courseSlug The slug of the course to retrieve the card url for
   */
  async GetCourseCard(courseSlug: string): Promise<Blob> {
    const response = await fetch(
      this.BuildUrl(`courses/training/cards/${courseSlug}`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return await response.blob();
  }

  /**
   * Get the mapping between a course id and it's name
   * @returns The mapping between a course id and the name of the course
   */
  async GetIdNameMapping(): Promise<Record<string, string>> {
    const response = await this.authFetch(
      this.BuildUrl(`courses/id-name-mapping`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.json()) as Record<string, string>;
  }

  /**
   * Get the certificate for the specified course.
   * @param courseId The course to get the certificate for
   */
  async GetCertificate(
    courseId: string
  ): Promise<{ filename: string; content: Blob }> {
    const response = await this.authFetch(
      this.BuildUrl(`courses/certificate/${courseId}`),
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        // Set the timezone offset to ensure the certificate is generated for the right time zone
        body: `${(new Date().getTimezoneOffset() / 60) * -1}`,
      }
    );

    if (!response.ok) throw Error();

    // Set the filename
    let filename = 's3g-certificate.pdf';
    if (response.headers.has('content-disposition')) {
      const header = response.headers.get('content-disposition')!;
      const match = header.match(/filename\*?=["']?([^;"']+)/);
      filename = match ? match[1] : filename;
    }

    // Return the filename and content
    return {
      filename: filename,
      content: await response.blob(),
    };
  }
}
